import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement,
  Tooltip, Legend, Filler,
} from 'chart.js';

import { sans, mono, display, green, greenLight, greenDark, red, amber, coral, coralSoft, coralHover, themes, ThemeContext, useTheme, CurrencyContext, useCurrency } from './tokens';
import { getDeadlineStatus, fmtC, fmtCompactC } from './helpers';
import { MONTHS, getMonthData } from './data/monthlyData';
import { DEPARTMENTS, getCompanyBudget, getDepartmentCashFlow, BUDGET_BY_DEPT } from './data/companyGL';
import { MONTHLY_ACTUALS } from './data/monthlyActuals';
import { CASH_ACTUALS, ACCOUNT_META, sumCashActual, getNetCashChange, getAdjustments } from './data/cashFlowData';
import { buildCashHierarchy } from './data/cashFlowHierarchy';
import CashView from './components/CashView';

import Kpi from './components/Kpi';
import CloseChecklist from './components/CloseChecklist';
import TaskWorkspace from './components/TaskWorkspace';
import SectionPanel from './components/SectionPanel';
import CashWorkspace from './components/CashWorkspace';
import { FS_SECTIONS } from './components/CloseChecklist';
import PanelSidebar from './components/PanelSidebar';
import PanelDetailView from './components/PanelDetailView';
import PanelConfigScreen from './components/PanelConfigScreen';
import TBUploadPanel from './components/TBUploadPanel';
import PANEL_CATALOG, { LEGACY_SECTION_MAP, getPanelById } from './data/panelCatalog';
import GLVarianceTable from './components/GLVarianceTable';
import PLVarianceAnalysis from './components/PLVarianceAnalysis';
import CashCloseView from './components/CashCloseView';
import ReviewDashboard from './components/ReviewDashboard';
import BudgetTable from './components/BudgetTable';
import BoardCeilingDashboard from './components/BoardCeilingDashboard';
import CashflowForecast from './components/CashflowForecast';
import Dashboard from './components/Dashboard';
import InvestorPage from './components/investor/InvestorPage';
import EquityDashboard from './components/equity/EquityDashboard';
import SettingsPage from './components/settings/SettingsPage';
import BankFeed from './components/BankFeed';
import { Settings, Eye, LayoutDashboard, ClipboardCheck, Landmark, TrendingUp, PieChart, Users } from 'lucide-react';
import ForecastView from './components/ForecastView';
import SageSyncButton from './components/SageSyncButton';
import { computeRunwayMonth } from './data/cashflowForecast';
import CompanySelector from './components/CompanySelector';
import TeamPanel from './components/TeamPanel';
import LoginPage, { MfaEnroll } from './components/LoginPage';
import { supabase } from './supabaseClient';
import { AuthProvider, useAuth } from './AuthContext';
import { CompanyProvider, useCompany } from './CompanyContext';
import { LanguageContext, useTranslation } from './i18n';
import useSupabaseData from './hooks/useSupabaseData';
import useCloseTasks from './hooks/useCloseTasks';
import { RevExpTrend, BudgetVarianceBars, CashFlowChart, AccountStatusDonut } from './components/Charts';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, Filler);

function ThemeToggle({ isDark, onToggle }) {
  const t = useTheme();
  return (
    <button onClick={onToggle} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} style={{
      border: 'none', background: 'transparent', borderRadius: 8,
      width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, color: t.textSecondary, transition: 'all 0.15s',
    }}>
      {isDark ? '\u2600' : '\u263E'}
    </button>
  );
}

export function CurrencyToggle({ currency, onToggle, fxRate, fxDate }) {
  const t = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
      <div style={{
        display: 'flex', borderRadius: 8, overflow: 'hidden',
        border: `1px solid ${t.border}`, background: t.inputBg, flexShrink: 0,
      }}>
        <button onClick={() => onToggle('CAD')} style={{
          padding: '6px 10px', border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: sans, letterSpacing: 0.3,
          background: currency === 'CAD' ? green : 'transparent',
          color: currency === 'CAD' ? '#fff' : t.textSecondary,
          transition: 'all 0.15s',
        }}>
          CAD
        </button>
        <button onClick={() => onToggle('USD')} style={{
          padding: '6px 10px', border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: sans, letterSpacing: 0.3,
          background: currency === 'USD' ? green : 'transparent',
          color: currency === 'USD' ? '#fff' : t.textSecondary,
          transition: 'all 0.15s',
        }}>
          USD
        </button>
      </div>
    </div>
  );
}

function LanguageToggle({ lang, onToggle }) {
  const t = useTheme();
  const other = lang === 'en' ? 'fr' : 'en';
  return (
    <button onClick={() => onToggle(other)} style={{
      padding: '6px 10px', border: 'none', borderRadius: 8,
      background: 'transparent', cursor: 'pointer', flexShrink: 0,
      fontSize: 11, fontWeight: 600, fontFamily: sans, letterSpacing: 0.3,
      color: t.textSecondary, transition: 'all 0.15s',
    }}>{other.toUpperCase()}</button>
  );
}

function MonthSelector({ selectedMonth, onChange }) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  const [selYear, setSelYear] = useState(selectedMonth.split('-')[0]);
  const current = MONTHS.find(m => m.key === selectedMonth);

  // Group months by year
  const years = [...new Set(MONTHS.map(m => m.key.split('-')[0]))];
  const monthsInYear = MONTHS.filter(m => m.key.startsWith(selYear));

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        fontSize: 11, background: t.inputBg, padding: '6px 14px', borderRadius: 20,
        color: t.textSecondary, fontWeight: 500, fontFamily: sans,
        border: `1px solid ${t.border}`, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {current?.label}
        <span style={{ fontSize: 8, marginLeft: 2 }}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 50,
          minWidth: 200, padding: 8,
        }}>
          {/* Year selector */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {years.map(y => (
              <button key={y} onClick={() => setSelYear(y)} style={{
                flex: 1, padding: '5px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, fontFamily: sans,
                background: selYear === y ? greenLight : t.inputBg,
                color: selYear === y ? green : t.textMuted,
              }}>{y}</button>
            ))}
          </div>
          {/* Month grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {monthsInYear.map(m => (
              <button key={m.key} onClick={() => { onChange(m.key); setOpen(false); }} style={{
                padding: '6px 4px', border: 'none', cursor: 'pointer', borderRadius: 6,
                fontSize: 11, fontFamily: sans, textAlign: 'center',
                background: m.key === selectedMonth ? greenLight : 'transparent',
                color: m.key === selectedMonth ? green : t.textPrimary,
                fontWeight: m.key === selectedMonth ? 600 : 400,
              }}>
                {m.short}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsdBanner() {
  const cc = useCurrency();
  if (cc.currency !== 'USD') return null;
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: '#EBF4FF', borderBottom: '1px solid #B3D4FC',
      padding: '4px 24px', fontSize: 11, fontFamily: sans, fontWeight: 500,
      color: '#2563EB', display: 'flex', alignItems: 'center', gap: 8,
      letterSpacing: 0.2,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: '#2563EB', flexShrink: 0 }} />
      Amounts displayed in USD
      <span style={{ fontFamily: mono, fontSize: 10, color: '#6B8EC7' }}>
        (BoC rate: {cc.fxRate.toFixed(4)})
      </span>
    </div>
  );
}

function CashRunwayStrip({ monthData, mockToday }) {
  const t = useTheme();
  const cc = useCurrency();

  // Cash position — prefer live Flinks bank feed, fall back to hardcoded
  // CFS values from Feb 2026 EOM. Refreshes on finkloz-bank-feed-updated.
  const [bankPosition, setBankPosition] = React.useState(null);
  React.useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
    const fetchPos = () => {
      fetch(`${BACKEND_URL}/api/bank-feed/flinks/cash-position?company_id=${COMPANY_ID}`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d?.connected) setBankPosition(d); else setBankPosition(null); })
        .catch(() => setBankPosition(null));
    };
    fetchPos();
    const onUpdate = () => fetchPos();
    window.addEventListener('finkloz-bank-feed-updated', onUpdate);
    return () => window.removeEventListener('finkloz-bank-feed-updated', onUpdate);
  }, []);

  const totalLiquidity = bankPosition?.totals_usd?.total ?? 35900000;
  const cashInBank = bankPosition?.totals_usd?.cash ?? 1822903;
  const investments = bankPosition?.totals_usd?.investments ?? (totalLiquidity - cashInBank);
  // Hardcoded burn from MONTHLY_ACTUALS — used as fallback only when the
  // live Sage burn endpoint hasn't returned yet (or for months with no
  // synced GL). Once liveBurn arrives we recompute from Operating values.
  const glMonthlyBurn = (() => {
    const months = Object.keys(MONTHLY_ACTUALS['Salaries & Payroll Taxes'] || {}).sort();
    const last6 = months.slice(-6);
    if (last6.length === 0) return 1245386; // fallback
    let totalOut = 0, totalIn = 0;
    for (const m of last6) {
      for (const [cat, data] of Object.entries(MONTHLY_ACTUALS)) {
        const amt = data[m] || 0;
        if (cat.startsWith('Interest') || cat.startsWith('Grants')) {
          totalIn += Math.abs(amt);
        } else {
          totalOut += amt;
        }
      }
    }
    return Math.round((totalOut - totalIn) / last6.length);
  })();

  // Live Operating burn (BOM−EOM excl. equity) from the Sage endpoint —
  // this matches what the Dashboard's Burn KPI card shows so the two
  // numbers are always consistent.
  const [liveOperatingAvg, setLiveOperatingAvg] = React.useState(null);
  React.useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
    // Trailing 6 months including the current in-progress month so the
    // top-strip burn KPI matches the Dashboard's chart window.
    const ref = new Date();
    ref.setDate(1);
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ref);
      d.setMonth(d.getMonth() - i);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    const fetchLive = () => {
      fetch(`${BACKEND_URL}/api/sage/burn-trend?company_id=${COMPANY_ID}&months=${months.join(',')}`)
        .then(r => (r.ok ? r.json() : null))
        .then(rows => {
          if (!Array.isArray(rows) || !rows.length) return;
          // Average operating_burn_usd over CLOSED months only. Sourced
          // from Close & Review via mockToday, with a 1-month offset (PMD's
          // books close ahead of where the Close & Review tab tracks). When
          // Close & Review is fully built, restore `setMonth(-1)`. Matches
          // the same logic in components/Dashboard.js.
          const ref = mockToday ? new Date(mockToday) : new Date();
          ref.setDate(1);
          const lastClosedYM = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, '0')}`;
          const have = rows.filter(r => r.has_data && r.operating_burn_usd != null && r.month <= lastClosedYM);
          if (!have.length) return;
          const avg = have.reduce((s, r) => s + r.operating_burn_usd, 0) / have.length;
          setLiveOperatingAvg(Math.round(avg));
        })
        .catch(() => {});
    };
    fetchLive();
    const onSync = () => fetchLive();
    window.addEventListener('finkloz-sage-sync-completed', onSync);
    return () => window.removeEventListener('finkloz-sage-sync-completed', onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockToday]);

  const monthlyBurn = liveOperatingAvg != null ? liveOperatingAvg : glMonthlyBurn;

  // Runway — forward-looking from the FP&A forecast, excluding the planned
  // Series B / C raises. Returns the actual cash-out month so the strip
  // shows "Jul 2028" rather than "27 mo". Falls back to trailing avg if the
  // forecast helper is unavailable for any reason.
  const projection = React.useMemo(() => {
    try { return computeRunwayMonth(totalLiquidity); }
    catch { return { cashOutLabel: null, monthsUntil: null }; }
  }, [totalLiquidity]);
  const runwayValue = projection.cashOutLabel || 'Beyond 2031';
  const runwaySub = projection.monthsUntil != null
    ? `${projection.monthsUntil} mo · no raise`
    : '6-yr forecast clears';
  const runwayColor = projection.monthsUntil == null ? green
    : projection.monthsUntil <= 6 ? red
    : projection.monthsUntil <= 12 ? amber
    : green;

  const items = [
    { label: 'Cash', value: fmtC(cashInBank, cc), color: green },
    { label: 'GICs & Savings', value: fmtC(investments, cc), color: green },
    { label: 'Burn', value: `${fmtC(monthlyBurn, cc)}/mo`, sub: '(6-mo avg)', color: red },
    { label: 'Runway', value: runwayValue, sub: runwaySub, color: runwayColor },
  ];

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 0,
      marginBottom: 12, paddingBottom: 8,
      borderBottom: `1px solid ${t.borderLight}`,
    }}>
      {items.map((item, i) => (
        <div key={i} style={{
          padding: '8px 14px',
          borderLeft: `3px solid ${item.color}`,
        }}>
          <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans, marginBottom: 3 }}>{item.label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: mono, color: item.color }}>{item.value}</span>
            {item.sub && <span style={{ fontSize: 8, color: t.textMuted, fontFamily: sans }}>{item.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function YearEndChecklist({ companyConfig, theme: t }) {
  const yearEndTasks = [
    { id: 'ye-1', category: 'Financial Statements', name: 'Prepare annual financial statements (BS, P&L, Cash Flow, Equity)', done: false },
    { id: 'ye-2', category: 'Financial Statements', name: 'Draft notes to financial statements', done: false },
    { id: 'ye-3', category: 'Financial Statements', name: 'Management Discussion & Analysis (MD&A)', done: false },
    { id: 'ye-4', category: 'Tax & Compliance', name: 'Prepare T2 corporate tax return workpapers', done: false },
    { id: 'ye-5', category: 'Tax & Compliance', name: 'Calculate SR&ED claim (coordinate with EY)', done: false },
    { id: 'ye-6', category: 'Tax & Compliance', name: 'Reconcile deferred tax assets / liabilities', done: false },
    { id: 'ye-7', category: 'Tax & Compliance', name: 'File annual provincial returns (REQ)', done: false },
    { id: 'ye-8', category: 'Audit Preparation', name: 'Prepare audit schedules & PBC list', done: false },
    { id: 'ye-9', category: 'Audit Preparation', name: 'Prepare confirmation letters (bank, legal, AR)', done: false },
    { id: 'ye-10', category: 'Audit Preparation', name: 'Roll forward fixed asset schedule with annual impairment review', done: false },
    { id: 'ye-11', category: 'Audit Preparation', name: 'Prepare stock-based compensation schedule (if applicable)', done: false },
    { id: 'ye-12', category: 'Adjustments', name: 'Record year-end accruals and provisions', done: false },
    { id: 'ye-13', category: 'Adjustments', name: 'Review contingent liabilities and commitments', done: false },
    { id: 'ye-14', category: 'Adjustments', name: 'Subsequent events review (post balance sheet date)', done: false },
    { id: 'ye-15', category: 'Grants & Incentives', name: 'Finalize grant receivable recognition', done: false },
    { id: 'ye-16', category: 'Grants & Incentives', name: 'Reconcile government assistance (IRAP, NRC, etc.)', done: false },
    { id: 'ye-17', category: 'Close & Lock', name: 'Final trial balance review with Controller', done: false },
    { id: 'ye-18', category: 'Close & Lock', name: 'Lock period in Sage Intacct', done: false },
  ];

  const categories = [...new Set(yearEndTasks.map(t => t.category))];

  return (
    <div>
      {categories.map(cat => (
        <div key={cat} style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6,
            color: t.textMuted, fontWeight: 600, fontFamily: sans,
            padding: '0 10px', marginBottom: 6,
          }}>
            {cat}
          </div>
          {yearEndTasks.filter(task => task.category === cat).map(task => (
            <div key={task.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', border: `1px solid ${t.border}`,
              borderRadius: 5, marginBottom: 2, background: t.bgCard,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: 3,
                border: `1.5px solid ${t.borderMedium}`,
                background: 'transparent', flexShrink: 0, cursor: 'pointer',
              }} />
              <span style={{ fontSize: 12, fontFamily: sans, color: t.textPrimary }}>{task.name}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AppContent({ companyConfig, activeProfile, onMonthChange, sbData, onLiveRateLoaded, headerControls, userInitials, onNavigateRef, isDark, setIsDark, lang, setLang, currency, setCurrency, hasMfaEnrolled, showMfaSetup, setShowMfaSetup }) {
  const t = useTheme();
  const cc = useCurrency();
  const tr = useTranslation();
  const [module, setModule] = useState('dashboard');
  // Expose setModule to parent via ref callback
  if (onNavigateRef) onNavigateRef.current = setModule;
  const [closeTab, setCloseTab] = useState('checklist');
  const [fpaTab, setFpaTab] = useState('actual-forecast');
  const [equityTab, setEquityTab] = useState('cap-table');
  const [budgetTab, setBudgetTab] = useState('variance');
  const [budgetPeriod, setBudgetPeriod] = useState('YTD');
  const [variancePeriod, setVariancePeriod] = useState('full');
  const [varianceComments, setVarianceComments] = useState(() => {
    try { return JSON.parse(localStorage.getItem('finkloz_variance_comments')) || {}; } catch { return {}; }
  });
  const [editingComment, setEditingComment] = useState(null); // category being edited
  const [commentDraft, setCommentDraft] = useState('');
  const [customStart, setCustomStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [customEnd, setCustomEnd] = useState(new Date().toISOString().slice(0, 10));
  const [varianceTab, setVarianceTab] = useState('pl-analysis');
  const [flagModal, setFlagModal] = useState(null); // { category, dept }
  const [flagQuestion, setFlagQuestion] = useState('');
  const [budgetFlags, setBudgetFlags] = useState([]);
  // ─── Live actuals from sage_gl_detail (Variance/Forecast vs Actual tab only) ──
  const [liveActuals, setLiveActuals] = useState({});
  const [liveActualsLoading, setLiveActualsLoading] = useState(false);

  // Map budget category → list of GL account codes (P&L only)
  const CATEGORY_TO_GL = React.useMemo(() => ({
    'Salaries & Payroll Taxes': ['61010', '61020', '61030', '61040', '61050', '61055'],
    'Employee Benefits & People': ['61060', '61070', '61080'],
    'R&D -- Manufacturing': ['51010', '51011', '51020', '51030', '51040', '51050', '51055', '51060', '51070'],
    'R&D -- Clinical & Preclinical': ['52100', '52110', '52120', '52200', '52240'],
    'Professional & Consulting': ['62010', '62110', '64010', '64020', '64050', '64060', '64070', '64090'],
    'Legal, IP & Audit': ['63010', '63020', '63021', '63030', '63050'],
    'Facilities & Rent': ['65010', '65020', '65021', '65022', '65030', '65040', '65050'],
    'Software, IT & Supplies': ['66010', '66020', '66030', '66040', '66050', '66060'],
    'Travel & Meals': ['67010', '67030', '67040'],
    'Bank Charges & Other': ['66070', '66080', '68010', '70010', '70020'],
    'Certifications & Compliance': ['53100'],
    'Interest Income': ['49400'],
    'Grants (iRAP, SR&ED)': ['49100', '49200'],
  }), []);

  // Map dept name → Sage codes (mirrors mobile)
  const DEPT_NAME_TO_SAGE = React.useMemo(() => ({
    'Operations': ['MA_01', 'MA_02', 'MA_04', 'SC_01', 'SC-01', 'OP-01'],
    'Mechanical R&D': ['RD_01', 'RD_03', 'RD_04'],
    'Electrical & Software': ['RD_02'],
    'Clinical & Regulatory': ['CL_00'],
    'Design Control & Testing': ['CL_01'],
    'Quality': ['QU_01'],
    'Intellectual Property': ['IP_01'],
    'IT': ['IT_01'],
    'Finances & Legal': ['FN_01'],
    'People & Culture': ['HR_01'],
    'Executive': ['EX_01'],
    // Mobile sub-depts → single Sage code
    'Machine Shop & Metrology': ['MA_01'], 'Cleanroom': ['MA_02'], 'Facilities': ['MA_04'],
    'Supply Chain': ['SC_01', 'SC-01'],
    'Mechanical Design': ['RD_01'], 'Internal Testing': ['RD_03'], 'Design': ['RD_04'],
  }), []);

  // Load budget flags from Supabase
  useEffect(() => {
    supabase.from('budget_flags').select('*').order('created_at', { ascending: false }).then(r => {
      if (r.data) setBudgetFlags(r.data);
    });
    const channel = supabase.channel('flags-web').on('postgres_changes', { event: '*', schema: 'public', table: 'budget_flags' }, () => {
      supabase.from('budget_flags').select('*').order('created_at', { ascending: false }).then(r => {
        if (r.data) setBudgetFlags(r.data);
      });
    }).subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  // Redirect restricted roles to their allowed module
  useEffect(() => {
    const role = activeProfile?.role;
    if ((role === 'investor' || role === 'board') && module !== 'investor') {
      setModule('investor');
    }
  }, [activeProfile?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const flaggedCategories = budgetFlags.map(f => f.category);
  const getFlagsForCategory = (cat) => budgetFlags.filter(f => f.category === cat);
  const [selectedMonth, setSelectedMonth] = useState('2026-02');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [closeView, setCloseView] = useState('panels'); // 'panels' (sidebar+detail) or 'board' (kanban)
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPanelConfig, setShowPanelConfig] = useState(false);
  const [showTBUpload, setShowTBUpload] = useState(false);
  // GL mappings from imported Trial Balance
  const [glMappings, setGlMappings] = useState(() => {
    try {
      const saved = localStorage.getItem(`finkloz_gl_mappings_${selectedMonth}`);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  // Active panels: stored per-company. null = all panels visible.
  // In production this will come from company_panel_config in Supabase.
  const [configuredPanels, setConfiguredPanels] = useState(() => {
    try {
      const saved = localStorage.getItem('finkloz_active_panels');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [selectedDept, setSelectedDept] = useState('all');

  // ─── Fetch live actuals when variance tab is opened (or filters change) ──
  useEffect(() => {
    if (fpaTab !== 'variance') return;
    const fetchLiveActuals = async () => {
      setLiveActualsLoading(true);
      const fiscalYear = 2026;
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // 1-12

      let query = supabase
        .from('sage_gl_detail')
        .select('account_no, debit, credit, month, department')
        .like('month', `${fiscalYear}-%`);

      if (variancePeriod === 'mtd') {
        const m = `${fiscalYear}-${String(currentMonth).padStart(2, '0')}`;
        query = query.eq('month', m);
      } else if (variancePeriod === 'ytd') {
        query = query.lte('month', `${fiscalYear}-${String(currentMonth).padStart(2, '0')}`);
      }
      // 'full' = no extra filter

      // Filter by dept if a specific one is selected
      if (selectedDept && selectedDept !== 'all' && DEPT_NAME_TO_SAGE[selectedDept]) {
        query = query.in('department', DEPT_NAME_TO_SAGE[selectedDept]);
      }

      const { data, error } = await query;
      if (error) { console.warn('Live actuals fetch error:', error); setLiveActualsLoading(false); return; }

      // Build account → category lookup
      const accToCat = {};
      Object.entries(CATEGORY_TO_GL).forEach(([cat, codes]) => {
        codes.forEach(c => { accToCat[c] = cat; });
      });

      // Sum by category
      const sums = {};
      (data || []).forEach(row => {
        const cat = accToCat[row.account_no];
        if (!cat) return;
        const net = (Number(row.debit) || 0) - (Number(row.credit) || 0);
        sums[cat] = (sums[cat] || 0) + net;
      });
      // Income categories should be displayed as positive (revenues are credits)
      ['Interest Income', 'Grants (iRAP, SR&ED)'].forEach(cat => {
        if (sums[cat] !== undefined) sums[cat] = Math.abs(sums[cat]);
      });

      setLiveActuals(sums);
      setLiveActualsLoading(false);
    };

    fetchLiveActuals();
    // Realtime subscription so changes in Sage propagate instantly
    const ch = supabase.channel('variance-live-actuals')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sage_gl_detail' }, fetchLiveActuals)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fpaTab, variancePeriod, selectedDept, CATEGORY_TO_GL, DEPT_NAME_TO_SAGE]);

  const handleMonthChange = (m) => {
    setSelectedMonth(m);
    if (onMonthChange) onMonthChange(m);
  };

  // Year-End detection: check if selected month matches fiscal year-end month
  const fyeMonth = companyConfig?.fiscalYearEndMonth || 12;
  const selectedMonthNum = Number(selectedMonth.split('-')[1]);
  const isYearEnd = selectedMonthNum === fyeMonth;

  const monthData = useMemo(() => getMonthData(selectedMonth), [selectedMonth]);

  // Load tasks from Supabase (persisted)
  const {
    tasks: taskList,
    toggleComplete,
    toggleReview,
    toggleBlocked,
    updateDeadline,
    addTask,
    removeTask,
  } = useCloseTasks(companyConfig?.companyId, selectedMonth, userInitials);

  // Fall back from yearend tab if not FYE month
  const [prevMonth, setPrevMonth] = useState(selectedMonth);
  if (selectedMonth !== prevMonth) {
    setPrevMonth(selectedMonth);
    setOpenTaskId(null);
    setSelectedPanelId(null);
    const newMonthNum = Number(selectedMonth.split('-')[1]);
    if (module === 'yearend' && newMonthNum !== fyeMonth) setModule('close');
  }

  // Mock "today" = 4th of the following month (close period)
  const [year, mon] = selectedMonth.split('-').map(Number);
  const nextMonth = mon === 12 ? `${year + 1}-01` : `${year}-${String(mon + 1).padStart(2, '0')}`;
  const mockToday = `${nextMonth}-04T12:00:00`;

  const done = taskList.filter(task => task.done).length;
  const total = taskList.length;
  const pct = Math.round((done / total) * 100);
  const overdue = taskList.filter(task => getDeadlineStatus(task.dueDate, task.done, mockToday) === 'overdue').length;
  const reviewed = taskList.filter(task => task.reviewed).length;
  const openTask = taskList.find(task => task.id === openTaskId);

  // Active panel IDs: null = show all panels from catalog
  // configuredPanels from localStorage (will be Supabase in production)
  const activePanelIds = configuredPanels;

  const handleTogglePanel = (panelId) => {
    setConfiguredPanels(prev => {
      // If null (all active), initialize with all IDs then toggle
      const allIds = PANEL_CATALOG.map(p => p.id);
      const current = prev ? new Set(prev) : new Set(allIds);
      if (current.has(panelId)) {
        current.delete(panelId);
      } else {
        current.add(panelId);
      }
      const next = [...current];
      localStorage.setItem('finkloz_active_panels', JSON.stringify(next));
      return next;
    });
  };

  // Add a new task to the current panel
  const handleAddTask = (taskName) => {
    if (!selectedPanelId || !taskName) return;
    const panel = getPanelById(selectedPanelId);
    if (!panel) return;
    addTask({
      name: taskName,
      section: panel.label,
      panelId: selectedPanelId,
      accounts: panel.glRange || null,
    });
  };

  // Remove a task
  const handleRemoveTask = (taskId) => {
    removeTask(taskId);
  };

  // Custom panels (stored in localStorage, will move to Supabase)
  const [customPanels, setCustomPanels] = useState(() => {
    try {
      const saved = localStorage.getItem('finkloz_custom_panels');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Register custom panels into the catalog at render time
  useMemo(() => {
    customPanels.forEach(cp => {
      if (!PANEL_CATALOG.find(p => p.id === cp.id)) {
        PANEL_CATALOG.push(cp);
      }
    });
  }, [customPanels]);

  const handleCreatePanel = (panelData) => {
    const slug = panelData.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
    const id = `custom-${slug}`;
    // Avoid duplicates
    if (PANEL_CATALOG.find(p => p.id === id)) return;
    const newPanel = {
      id,
      label: panelData.label,
      label_fr: '',
      statementType: panelData.statementType,
      category: panelData.category,
      panelType: panelData.panelType,
      glRange: panelData.glRange || '',
      sort: 999,
      isSystem: false,
    };
    PANEL_CATALOG.push(newPanel);
    const updated = [...customPanels, newPanel];
    setCustomPanels(updated);
    localStorage.setItem('finkloz_custom_panels', JSON.stringify(updated));
    // Auto-activate the new panel
    if (configuredPanels) {
      const next = [...configuredPanels, id];
      setConfiguredPanels(next);
      localStorage.setItem('finkloz_active_panels', JSON.stringify(next));
    }
  };

  const handleRemovePanel = (panelId) => {
    // Only allow removing custom panels
    const idx = PANEL_CATALOG.findIndex(p => p.id === panelId && !p.isSystem);
    if (idx === -1) return;
    PANEL_CATALOG.splice(idx, 1);
    const updated = customPanels.filter(p => p.id !== panelId);
    setCustomPanels(updated);
    localStorage.setItem('finkloz_custom_panels', JSON.stringify(updated));
    // Deactivate if in configured panels
    if (configuredPanels) {
      const next = configuredPanels.filter(id => id !== panelId);
      setConfiguredPanels(next);
      localStorage.setItem('finkloz_active_panels', JSON.stringify(next));
    }
    // Deselect if currently selected
    if (selectedPanelId === panelId) setSelectedPanelId(null);
  };

  // Get selected panel metadata and its tasks
  const selectedPanel = selectedPanelId ? getPanelById(selectedPanelId) : null;
  const selectedPanelTasks = useMemo(() => {
    if (!selectedPanelId) return [];
    return taskList.filter(task => {
      const pid = task.panelId || LEGACY_SECTION_MAP[task.section];
      return pid === selectedPanelId;
    });
  }, [taskList, selectedPanelId]);

  const priorLabel = monthData.priorMonth?.short + ' ' + monthData.priorMonth?.key.split('-')[0];
  const currentLabel = monthData.month.short + ' ' + monthData.month.key.split('-')[0];


  const pillBtn = (key, label, active, onClick) => (
    <button key={key} onClick={onClick} style={{
      padding: '5px 14px', borderRadius: 16, border: `1px solid ${active ? coral : t.border}`,
      background: active ? coralSoft : 'transparent',
      color: active ? coral : t.textSecondary,
      fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: sans,
    }}>
      {label}
    </button>
  );

  return (
    <div style={{
      fontFamily: sans, maxWidth: 1280, margin: '0 auto', padding: '20px 24px 20px 174px',
      color: t.textPrimary, minHeight: '100vh',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 40 }} />
          {companyConfig?.companyName && (
            <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 400, fontFamily: sans, marginTop: 2 }}>
              {companyConfig.companyName}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {headerControls}
        </div>
      </div>

      {/* Empty state for companies with no data */}
      {!sbData?.data?.gics?.length && !sbData?.data?.apAging?.length && !sbData?.data?.receivables?.length && !sbData?.data?.capitalRaises?.length && !Object.keys(sbData?.data?.metrics || {}).length ? (
        <div style={{
          textAlign: 'center', padding: '80px 24px', marginTop: 20,
          background: t.cardBg, borderRadius: 14, border: `1px solid ${t.border}`,
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>No data yet</div>
          <div style={{ fontSize: 13, color: t.textSecondary, maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
            Connect your ERP (like {companyConfig?.erp || 'Sage Intacct'}) to start importing financial data
            for <strong>{companyConfig?.companyName}</strong>. Once connected, your dashboard will populate automatically.
          </div>
        </div>
      ) : (
      <>
      {/* Side Navigation */}
      {(() => {
        const allModules = [
          { key: 'dashboard', label: tr('nav.overview'), icon: LayoutDashboard },
          { key: 'close', label: tr('nav.close'), icon: ClipboardCheck },
          { key: 'bankfeed', label: 'Bank Feed', icon: Landmark },
          { key: 'fpa', label: tr('nav.fpa'), icon: TrendingUp },
          { key: 'equity', label: 'Equity', icon: PieChart },
          { key: 'investor', label: tr('nav.investor'), icon: Users },
        ];
        const role = activeProfile?.role;
        const investorPreview = new URLSearchParams(window.location.search).get('preview') === 'investor';
        const visibleModules = allModules.filter(m => {
          if (investorPreview) return m.key === 'investor';
          if (role === 'investor') return m.key === 'investor';
          if (role === 'auditor') return ['dashboard', 'close', 'fpa', 'investor'].includes(m.key);
          if (role === 'board') return ['investor'].includes(m.key);
          return true;
        });
        return (
          <div style={{
            position: 'fixed', left: 0, top: 0, width: 150, height: '100vh',
            overflowY: 'auto', background: t.bgCard, borderRight: `1px solid ${t.border}`,
            zIndex: 10, display: 'flex', flexDirection: 'column', gap: 2,
            padding: '100px 8px 0 8px',
          }}>
            {visibleModules.map(m => {
              const Icon = m.icon;
              const isActive = module === m.key;
              // Sub-items per module
              const subItems = {
                close: [
                  { key: 'checklist', label: 'Checklist' },
                  { key: 'review', label: 'Review' },
                  { key: 'pl-analysis', label: 'P&L Variance' },
                ],
                fpa: [
                  { key: 'actual-forecast', label: 'Actual Forecast' },
                  { key: 'scenario-forecast', label: 'Scenario Forecast' },
                  { key: 'variance', label: 'Forecast vs. Actual' },
                ],
                equity: [
                  { key: 'cap-table', label: 'Cap Table' },
                  { key: 'options', label: 'Options' },
                  { key: 'sbc', label: 'SBC Expense' },
                  { key: 'reports', label: 'Reports' },
                ],
                investor: [
                  { key: 'inv-overview', label: 'Overview', scroll: true },
                  { key: 'inv-alerts', label: 'Updates', scroll: true },
                  { key: 'inv-metrics', label: 'Financial', scroll: true },
                  { key: 'inv-capital', label: 'Capital', scroll: true },
                  { key: 'inv-roadmap', label: 'Roadmap', scroll: true },
                  { key: 'inv-risks', label: 'Risks', scroll: true },
                  { key: 'inv-financials', label: 'Highlights', scroll: true },
                  { key: 'inv-documents', label: 'Documents', scroll: true },
                ],
              };
              const subs = subItems[m.key];
              const activeSubTab = m.key === 'close' ? closeTab : m.key === 'fpa' ? fpaTab : m.key === 'equity' ? equityTab : null;
              const setSubTab = m.key === 'close' ? setCloseTab : m.key === 'fpa' ? setFpaTab : m.key === 'equity' ? setEquityTab : null;

              return (
                <React.Fragment key={m.key}>
                  <div onClick={() => { setModule(m.key); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                    fontSize: 12, fontWeight: isActive ? 600 : 400, fontFamily: sans,
                    background: isActive ? coral : 'transparent',
                    color: isActive ? '#FFF8F1' : t.textMuted,
                    borderLeft: isActive ? `3px solid ${coral}` : '3px solid transparent',
                    borderRadius: isActive ? 6 : 0,
                    transition: 'background 0.15s, color 0.15s',
                  }}>
                    <Icon size={15} />
                    {m.label}
                  </div>
                  {/* Sub-items expand inline */}
                  {subs && (
                    <div style={{
                      paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 1,
                      maxHeight: isActive ? 300 : 0, overflow: 'hidden',
                      opacity: isActive ? 1 : 0,
                      transition: 'max-height 0.35s ease, opacity 0.25s ease',
                    }}>
                      {subs.map(s => {
                        const isSubActive = s.scroll ? false : activeSubTab === s.key;
                        return (
                          <div key={s.key} onClick={(e) => {
                            e.stopPropagation();
                            if (s.scroll) {
                              const el = document.getElementById(s.key);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            } else if (setSubTab) {
                              setSubTab(s.key);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }} style={{
                            padding: '5px 10px', borderRadius: 4, cursor: 'pointer',
                            fontSize: 10, fontFamily: sans,
                            color: isSubActive ? coral : t.textMuted,
                            fontWeight: isSubActive ? 600 : 400,
                            background: isSubActive ? coralSoft : 'transparent',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { if (!isSubActive) e.currentTarget.style.color = coral; }}
                          onMouseLeave={e => { if (!isSubActive) e.currentTarget.style.color = t.textMuted; }}
                          >
                            {s.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div style={{ marginTop: 'auto', padding: '16px 12px 20px', borderTop: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans }}>
                {companyConfig?.companyName || 'Demo Medical Devices'}
              </div>
              <div style={{ fontSize: 8, color: t.textFaint, fontFamily: sans, marginTop: 2 }}>
                Powered by FinKloz
              </div>
            </div>
          </div>
        );
      })()}
      <div>

      {/* Cash / Burn / Runway — critical for pre-revenue medtech */}
      {!['investor', 'board'].includes(activeProfile?.role) && <CashRunwayStrip monthData={monthData} mockToday={mockToday} />}

      {/* DASHBOARD */}
      {module === 'dashboard' && (
        <Dashboard monthData={monthData} taskList={taskList} mockToday={mockToday} onNavigate={setModule} sbData={sbData} onLiveRateLoaded={onLiveRateLoaded} />
      )}

      {/* MONTH-END CLOSE */}
      {module === 'close' && (
        <div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14,
            marginBottom: 20,
          }}>
            {[
              { label: 'Progress', value: `${pct}%`, color: pct === 100 ? green : pct === 0 ? t.textMuted : amber },
              { label: 'Completed', value: `${done}/${total}`, sub: `${total - done} remaining`, subColor: green, color: green },
              { label: 'Reviewed', value: `${reviewed}/${total}`, sub: `${done - reviewed} pending review`, subColor: amber, color: amber },
              { label: 'Overdue', value: overdue, sub: overdue > 0 ? 'Needs attention' : 'All clear', subColor: overdue > 0 ? red : green, color: overdue > 0 ? red : green },
              { label: 'Est. close', value: `${monthData.month.short} 6`, sub: done === total ? 'Closed' : '+1 day vs target', subColor: done === total ? green : amber, color: done === total ? green : amber },
            ].map((kpi, i) => (
              <div key={i} style={{
                borderLeft: `3px solid ${kpi.color || t.border}`,
                overflow: 'hidden',
              }}>
                <Kpi label={kpi.label} value={kpi.value} sub={kpi.sub} subColor={kpi.subColor} />
              </div>
            ))}
          </div>

          <div style={{ height: 3, background: t.border, borderRadius: 2, marginBottom: 20 }}>
            <div style={{ height: 3, width: `${pct}%`, background: green, borderRadius: 2, transition: 'width 0.3s' }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
            {/* Action buttons for checklist tab */}
            {closeTab === 'checklist' && (
              <>
                <button onClick={() => setShowTBUpload(true)} style={{
                  border: 'none', background: coral, borderRadius: 6,
                  padding: '6px 14px', fontSize: 10, fontFamily: sans, cursor: 'pointer',
                  color: '#FFF8F1', marginLeft: 6, fontWeight: 500,
                }} title="Import Trial Balance">
                  Import TB
                </button>
                <button onClick={() => setShowPanelConfig(true)} style={{
                  border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 6,
                  padding: '4px 10px', fontSize: 10, fontFamily: sans, cursor: 'pointer',
                  color: t.textMuted, marginLeft: 4,
                }} title="Configure panels">
                  Settings
                </button>
              </>
            )}

            <div style={{ marginLeft: 'auto' }}>
              <MonthSelector selectedMonth={selectedMonth} onChange={handleMonthChange} />
            </div>
          </div>

          {closeTab === 'checklist' && closeView === 'panels' && (
            <div style={{
              display: 'flex', border: `1px solid ${t.border}`, borderRadius: 10,
              overflow: 'hidden', minHeight: 500, background: t.bgCard || '#fff',
            }}>
              <PanelSidebar
                tasks={taskList}
                activePanelIds={activePanelIds}
                selectedPanelId={selectedPanelId}
                onSelectPanel={(id) => setSelectedPanelId(id)}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                glMappings={glMappings}
              />
              {selectedPanel ? (
                <PanelDetailView
                  panel={selectedPanel}
                  tasks={selectedPanelTasks}
                  onToggleComplete={toggleComplete}
                  onToggleReview={toggleReview}
                  onToggleBlocked={toggleBlocked}
                  onUpdateDeadline={updateDeadline}
                  onOpenTask={(id) => setOpenTaskId(id)}
                  onAddTask={handleAddTask}
                  onRemoveTask={handleRemoveTask}
                  mockToday={mockToday}
                  userInitials={userInitials}
                  glMappings={glMappings}
                  companyId={companyConfig?.companyId}
                  selectedMonth={selectedMonth}
                />
              ) : (
                <div style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: t.textFaint, fontSize: 13, fontFamily: sans,
                }}>
                  Select a panel from the sidebar to view tasks
                </div>
              )}
            </div>
          )}

          {closeTab === 'review' && (
            <ReviewDashboard
              tasks={taskList}
              activePanelIds={activePanelIds}
              onToggleReview={toggleReview}
              onSelectPanel={(id) => { setSelectedPanelId(id); setCloseTab('checklist'); setCloseView('panels'); }}
              mockToday={mockToday}
              userInitials={userInitials}
            />
          )}
          {closeTab === 'pl-analysis' && <PLVarianceAnalysis glHistory={monthData.glHistory} currentLabel={monthData.month.short} />}
          {closeTab === 'yearend' && isYearEnd && (
            <>
              <div style={{
                background: `linear-gradient(135deg, ${greenLight}, #FFF3E0)`,
                borderRadius: 10, padding: '20px 24px', marginBottom: 20,
                border: `1px solid ${t.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#212121', fontFamily: sans }}>Year-End Close</span>
                  <span style={{
                    fontSize: 10, padding: '2px 10px', borderRadius: 12,
                    background: amber + '22', color: amber, fontWeight: 600, fontFamily: sans,
                  }}>
                    FYE {MONTHS.find(m => Number(m.key.split('-')[1]) === fyeMonth)?.short || ''}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#757575', fontFamily: sans, lineHeight: 1.5 }}>
                  Year-end procedures in addition to the regular month-end close.
                </div>
              </div>
              <YearEndChecklist companyConfig={companyConfig} theme={t} />
            </>
          )}
        </div>
      )}

      {/* FP&A (Budget + Forecast) */}
      {module === 'fpa' && (<>
        {/* Department selector hidden on the Forecast vs. Actual tab —
            the cash close view has its own per-dept drilldown inline. */}
        {fpaTab !== 'variance' && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, alignItems: 'center', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans }}>Department</span>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                style={{
                  fontSize: 12, fontFamily: sans, fontWeight: 500,
                  padding: '5px 28px 5px 12px', borderRadius: 16,
                  border: `1px solid ${selectedDept === 'all' ? t.border : green}`,
                  background: selectedDept === 'all' ? t.inputBg : coralSoft,
                  color: selectedDept === 'all' ? t.textSecondary : coral,
                  cursor: 'pointer', outline: 'none',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%239E9E9E'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                }}
              >
                {DEPARTMENTS.map(d => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      {fpaTab === 'actual-forecast' && (
        <>
          <div style={{ marginBottom: 32 }}>
            <BoardCeilingDashboard />
          </div>
          <CashflowForecast />
          <ForecastView mode="actual" />
        </>
      )}
      {fpaTab === 'scenario-forecast' && <ForecastView mode="scenario" />}
      {fpaTab === 'variance' && (() => {
        const deptBudgetRaw = getCompanyBudget(selectedDept);

        // Map budget categories to forecast line items
        const budgetToForecastMap = {
          'Salaries & Payroll Taxes': 'payroll',
          'R&D -- Manufacturing': 'rdMfg',
          'R&D -- Clinical & Preclinical': 'rdClinical',
          'Professional & Consulting': 'professional',
          'Legal, IP & Audit': 'professional',
          'Employee Benefits & People': 'hrPeople',
          'Facilities & Rent': 'facilities',
          'Software, IT & Supplies': 'softwareIT',
          'Travel & Meals': 'hrPeople',
          'Bank Charges & Other': 'fxOther',
          'Certifications & Compliance': 'fxOther',
          'Interest Income': 'interest',
          'Grants (iRAP, SR&ED)': 'grants',
        };

        // Get forecast data for current year (2026)
        const forecastYear = 2026;
        const forecastRaw = (() => {
          const base = {
            interestIncome: 1952151, grants: 90992, payroll: 6875000,
            rdManufacturing: 3611264, rdClinical: 593617, professional: 1117002,
            facilities: 590694, softwareIT: 928297, hrPeople: 394344,
            depreciation: 753267, fxOther: 2478698,
          };
          const yearFactor = 1 + (forecastYear - 2025) * 0.05;
          return {
            interest: Math.round(base.interestIncome * 0.95),
            grants: Math.round(base.grants),
            payroll: Math.round(base.payroll * yearFactor),
            rdMfg: Math.round(base.rdManufacturing * yearFactor),
            rdClinical: Math.round(base.rdClinical * yearFactor),
            professional: Math.round(base.professional * yearFactor),
            facilities: Math.round(base.facilities * yearFactor),
            softwareIT: Math.round(base.softwareIT * yearFactor),
            hrPeople: Math.round(base.hrPeople * yearFactor),
            depreciation: Math.round(base.depreciation * yearFactor),
            fxOther: Math.round(base.fxOther * yearFactor),
          };
        })();

        // Aggregate forecast by budget category
        const forecastByCategory = {};
        for (const [cat, fKey] of Object.entries(budgetToForecastMap)) {
          forecastByCategory[cat] = (forecastByCategory[cat] || 0) + (forecastRaw[fKey] || 0);
        }

        const deptBudget = deptBudgetRaw.map(b => ({
          ...b,
          label: b.category,
          // Live actual from sage_gl_detail (Supabase) — fallback to static if not available
          totalActual: liveActuals[b.category] !== undefined ? liveActuals[b.category] : (b.actual || 0),
          forecastAmount: forecastByCategory[b.category] || 0,
        }));

        const expenseRows = deptBudget.filter(b => !b.isIncome);
        const incomeRows = deptBudget.filter(b => b.isIncome);
        const totalBudgetExp = expenseRows.reduce((s, b) => s + b.budget, 0);
        const totalForecastExp = expenseRows.reduce((s, b) => s + b.forecastAmount, 0);
        const totalActualExp = expenseRows.reduce((s, b) => s + b.totalActual, 0);
        const totalBudgetInc = incomeRows.reduce((s, b) => s + b.budget, 0);
        const totalForecastInc = incomeRows.reduce((s, b) => s + b.forecastAmount, 0);

        const allRows = [...expenseRows, ...incomeRows];

        return (
          <div data-dept={selectedDept}>
            {/* Cash close (View A + View B) — replaces P&L-basis with cash-basis */}
            <div style={{ marginBottom: 28 }}>
              <CashCloseView lastClosedYM={'2026-03'} />
            </div>

          </div>
        );
      })()}
      </>)}

      {/* INVESTOR */}
      {module === 'equity' && (
        <EquityDashboard companyId={companyConfig?.companyId} activeTab={equityTab} onTabChange={setEquityTab} />
      )}

      {module === 'investor' && (
        <InvestorPage sbData={sbData} />
      )}

      {/* SETTINGS */}
      {module === 'settings' && (
        <SettingsPage
          isDark={isDark} setIsDark={setIsDark}
          lang={lang} setLang={setLang}
          currency={currency} setCurrency={setCurrency}
          hasMfaEnrolled={hasMfaEnrolled} showMfaSetup={showMfaSetup} setShowMfaSetup={setShowMfaSetup}
        />
      )}

      {/* Section Workspace */}
      {openSectionId && (() => {
        const fs = FS_SECTIONS.find(s => s.id === openSectionId);
        const sectionTasks = taskList.filter(task => fs && fs.oldSections.includes(task.section));
        if (!fs) return null;

        const overlay = (
          <div onClick={() => setOpenSectionId(null)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: t.overlay, zIndex: 99,
          }} />
        );

        // Cash gets its custom workspace
        if (openSectionId === 'cash') {
          return (
            <>
              {overlay}
              <CashWorkspace
                tasks={sectionTasks}
                onClose={() => setOpenSectionId(null)}
                onToggleComplete={toggleComplete}
                onToggleReview={toggleReview}
                onToggleBlocked={toggleBlocked}
                onUpdateDeadline={updateDeadline}
                mockToday={mockToday}
                userInitials={userInitials}
                companyId={companyConfig?.companyId}
                selectedMonth={selectedMonth}
              />
            </>
          );
        }

        // All other sections get the generic panel
        return (
          <>
            {overlay}
            <SectionPanel
              fsSection={fs}
              tasks={sectionTasks}
              onClose={() => setOpenSectionId(null)}
              onToggleComplete={toggleComplete}
              onToggleReview={toggleReview}
              onToggleBlocked={toggleBlocked}
              onUpdateDeadline={updateDeadline}
              mockToday={mockToday}
              userInitials={userInitials}
            />
          </>
        );
      })()}

      {/* TB Upload Modal */}
      {showTBUpload && (
        <TBUploadPanel
          activePanelIds={activePanelIds}
          selectedMonth={selectedMonth}
          onConfirm={(result) => setGlMappings(result)}
          onCreatePanel={handleCreatePanel}
          onClose={() => setShowTBUpload(false)}
        />
      )}

      {/* Panel Configuration Modal */}
      {showPanelConfig && (
        <PanelConfigScreen
          activePanels={configuredPanels}
          onTogglePanel={handleTogglePanel}
          onCreatePanel={handleCreatePanel}
          onRemovePanel={handleRemovePanel}
          onClose={() => setShowPanelConfig(false)}
        />
      )}
      </div>{/* end marginLeft wrapper */}
      </>
      )}
    </div>
  );
}

// PasswordGate removed — replaced by Supabase Auth (see LoginPage.js)

// Bank of Canada USD/CAD month-end noon rates (from BoC Valet API)
// Will be fetched from backend /fx/month-end-rates once connected
const BOC_MONTH_END_RATES = {
  '2025-01': 1.4392, '2025-02': 1.4373, '2025-03': 1.4385, '2025-04': 1.3851,
  '2025-05': 1.3692, '2025-06': 1.3731, '2025-07': 1.3838, '2025-08': 1.3498,
  '2025-09': 1.3498, '2025-10': 1.3862, '2025-11': 1.4019, '2025-12': 1.4386,
  '2026-01': 1.4404, '2026-02': 1.4416, '2026-03': 1.3718,
};

// Derive 3-letter initials from email or user metadata
function getUserInitials(user) {
  if (!user) return '???';
  // Try user_metadata.full_name first
  const name = user.user_metadata?.full_name || user.user_metadata?.name;
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1].slice(0, 2)).toUpperCase();
    }
    return parts[0].slice(0, 3).toUpperCase();
  }
  // Fallback: first 3 chars of email username
  const prefix = (user.email || '').split('@')[0].replace(/[^a-zA-Z]/g, '');
  return prefix.slice(0, 3).toUpperCase() || '???';
}

// ─── MFA Verify Form (enter 6-digit TOTP code) ─────────────────────────────────
function MfaVerifyForm({ onSuccess, onSignOut }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter a 6-digit code'); return; }
    setLoading(true);
    setError('');
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = factors?.totp?.[0];
      if (!totp) { setError('No authenticator found'); setLoading(false); return; }
      const { data: challenge } = await supabase.auth.mfa.challenge({ factorId: totp.id });
      const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: challenge.id, code });
      if (verifyErr) { setError('Invalid code. Try again.'); setLoading(false); return; }
      // MFA verified -- refresh session to get AAL2 token, then reload
      await supabase.auth.refreshSession();
      // Small delay to let Supabase propagate the new session
      setTimeout(() => window.location.reload(), 300);
    } catch (e) { setError('Verification failed'); setLoading(false); }
  };

  return (
    <div>
      <input
        type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
        onKeyDown={e => e.key === 'Enter' && handleVerify()}
        placeholder="000000"
        style={{ width: '100%', padding: '12px', fontSize: 24, fontFamily: mono, textAlign: 'center', letterSpacing: 8, border: `1.5px solid ${error ? red : 'rgba(15,42,36,0.15)'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box', background: '#FFFBF6' }}
      />
      {error && <div style={{ color: red, fontSize: 12, marginTop: 8, fontFamily: sans }}>{error}</div>}
      <button onClick={handleVerify} disabled={loading} style={{ marginTop: 16, width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, background: loading ? '#9EACA7' : coral, color: '#FFF8F1', border: 'none', borderRadius: 6, cursor: loading ? 'default' : 'pointer', fontFamily: sans }}>
        {loading ? 'Verifying...' : 'Verify'}
      </button>
      <button onClick={onSignOut} style={{ marginTop: 12, background: 'none', border: 'none', color: '#6B7B77', fontSize: 12, cursor: 'pointer', fontFamily: sans }}>Sign out</button>
    </div>
  );
}

// ─── MFA Enroll Form (scan QR code + verify) ────────────────────────────────────
function MfaEnrollForm({ onSuccess }) {
  const [qr, setQr] = useState(null);
  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('loading'); // loading | scan | verify

  useEffect(() => {
    (async () => {
      try {
        const { data, error: enrollErr } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'FinKloz Authenticator' });
        if (enrollErr) { setError('Failed to set up 2FA'); setStep('error'); return; }
        setQr(data.totp.qr_code);
        setFactorId(data.id);
        setStep('scan');
      } catch { setError('Failed to set up 2FA'); setStep('error'); }
    })();
  }, []);

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter a 6-digit code'); return; }
    setLoading(true);
    setError('');
    try {
      const { data: challenge } = await supabase.auth.mfa.challenge({ factorId });
      const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code });
      if (verifyErr) { setError('Invalid code. Try again.'); setLoading(false); return; }
      await supabase.auth.refreshSession();
      setTimeout(() => window.location.reload(), 300);
    } catch { setError('Verification failed'); setLoading(false); }
  };

  if (step === 'loading') return <div style={{ padding: 20, color: '#6B7B77', fontFamily: sans }}>Setting up 2FA...</div>;
  if (step === 'error') return <div style={{ padding: 20, color: red, fontFamily: sans }}>{error}</div>;

  return (
    <div>
      {qr && <img src={qr} alt="QR Code" style={{ width: 200, height: 200, margin: '0 auto 16px', display: 'block', borderRadius: 8 }} />}
      <div style={{ fontSize: 11, color: '#6B7B77', fontFamily: sans, marginBottom: 16 }}>
        Scan with your authenticator app, then enter the 6-digit code below
      </div>
      <input
        type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
        onKeyDown={e => e.key === 'Enter' && handleVerify()}
        placeholder="000000"
        style={{ width: '100%', padding: '12px', fontSize: 24, fontFamily: mono, textAlign: 'center', letterSpacing: 8, border: `1.5px solid ${error ? red : 'rgba(15,42,36,0.15)'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box', background: '#FFFBF6' }}
      />
      {error && <div style={{ color: red, fontSize: 12, marginTop: 8, fontFamily: sans }}>{error}</div>}
      <button onClick={handleVerify} disabled={loading} style={{ marginTop: 16, width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, background: loading ? '#9EACA7' : coral, color: '#FFF8F1', border: 'none', borderRadius: 6, cursor: loading ? 'default' : 'pointer', fontFamily: sans }}>
        {loading ? 'Verifying...' : 'Activate 2FA'}
      </button>
    </div>
  );
}

function AppShell() {
  const { user, signOut, loading, needsMfaVerify, hasMfaEnrolled } = useAuth();
  const { activeCompany: companyConfig, activeProfile, loading: companyLoading, setActiveCompany } = useCompany();
  const sbData = useSupabaseData(user, companyConfig?.companyId);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('finkloz_theme') === 'dark');
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const navigateRef = React.useRef(null);
  // Default currency is USD (matches the investor preference). Users can
  // toggle to CAD and the choice persists per browser.
  const [currency, setCurrency] = useState(() => localStorage.getItem('finkloz_currency') || 'USD');
  const [lang, setLang] = useState(() => localStorage.getItem('closely_lang') || 'en');
  const theme = isDark ? themes.dark : themes.light;

  // FX rate: use live rate from ECB when available, fallback to BOC month-end rates
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonthForFx, setSelectedMonthForFx] = useState(currentMonth);
  const [liveRate, setLiveRate] = useState(null);
  const bocRate = BOC_MONTH_END_RATES[selectedMonthForFx] || 1.3718;
  // Use live rate for current month, BOC rate for historical months
  const fxRate = (selectedMonthForFx === currentMonth && liveRate) ? liveRate : bocRate;
  const setCurrencyPersist = (c) => {
    setCurrency(c);
    try { localStorage.setItem('finkloz_currency', c); } catch { /* ignore */ }
  };
  const currencyCtx = { currency, setCurrency: setCurrencyPersist, fxRate, fxDate: selectedMonthForFx };

  // Clean up old session auth
  useEffect(() => { sessionStorage.removeItem('closely_auth'); }, []);

  // Track login for history (must be before early returns)
  React.useEffect(() => {
    if (user?.email) {
      const history = JSON.parse(localStorage.getItem('finkloz_login_history') || '[]');
      const last = history[0];
      const now = new Date().toISOString();
      if (!last || (new Date(now) - new Date(last.timestamp)) > 60000) {
        history.unshift({
          timestamp: now,
          email: user.email,
          status: 'success',
          device: navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Firefox') ? 'Firefox' : navigator.userAgent.includes('Safari') ? 'Safari' : 'Browser',
        });
        if (history.length > 50) history.length = 50;
        localStorage.setItem('finkloz_login_history', JSON.stringify(history));
      }
    }
  }, [user?.email]);

  if (loading || companyLoading) return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 40 }} />
      </div>
    </div>
  );
  if (!user) return <LoginPage />;

  // SECURITY: Mandatory MFA enforcement
  // Localhost dev escape hatch — production still enforces MFA fully.
  // This is safe because localhost is only reachable from your own machine.
  // MFA temporarily disabled — re-enable by restoring this check once the
  // MfaVerify reload bug is fixed. See LoginPage.js for the matching bypass.
  // Original:
  //   const isLocalDev = typeof window !== 'undefined'
  //     && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const isLocalDev = true;

  // If user has MFA enrolled but hasn't verified this session, block access
  if (needsMfaVerify && !isLocalDev) {
    return (
      <ThemeContext.Provider value={theme}>
        <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: theme.bgCard, borderRadius: 12, padding: '40px 36px', width: 360, boxShadow: '0 4px 24px rgba(15,42,36,0.08)', textAlign: 'center' }}>
            <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 40, marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 600, color: theme.textPrimary, fontFamily: sans, marginBottom: 8 }}>Verify your identity</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, fontFamily: sans, marginBottom: 20 }}>Enter the 6-digit code from your authenticator app</div>
            <MfaVerifyForm onSuccess={() => window.location.reload()} onSignOut={signOut} />
          </div>
        </div>
      </ThemeContext.Provider>
    );
  }

  // If MFA is not enrolled yet, force enrollment before any access
  // (skipped on localhost for dev convenience)
  if (!hasMfaEnrolled && !isLocalDev) {
    return (
      <ThemeContext.Provider value={theme}>
        <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: theme.bgCard, borderRadius: 12, padding: '40px 36px', width: 400, boxShadow: '0 4px 24px rgba(15,42,36,0.08)', textAlign: 'center' }}>
            <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 40, marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 600, color: theme.textPrimary, fontFamily: sans, marginBottom: 8 }}>Set up two-factor authentication</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, fontFamily: sans, marginBottom: 20 }}>
              FinKloz requires 2FA to protect your financial data. Scan the QR code with Google Authenticator, Microsoft Authenticator, or 1Password.
            </div>
            <MfaEnrollForm onSuccess={() => window.location.reload()} />
            <button onClick={signOut} style={{ marginTop: 16, background: 'none', border: 'none', color: theme.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: sans }}>
              Sign out
            </button>
          </div>
        </div>
      </ThemeContext.Provider>
    );
  }

  if (!companyConfig) return <CompanySelector />;

  // Investor preview mode — clean view without admin controls
  const isPreview = new URLSearchParams(window.location.search).get('preview') === 'investor';
  if (isPreview) {
    return (
      <ThemeContext.Provider value={theme}>
        <LanguageContext.Provider value={lang}>
        <CurrencyContext.Provider value={currencyCtx}>
          <div style={{ background: theme.bg, minHeight: '100vh' }}>
            <div style={{
              background: green, color: '#fff', padding: '6px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontFamily: sans, fontSize: 11, fontWeight: 600,
            }}>
              <span>Investor Preview -- This is what investors see</span>
              <button onClick={() => window.close()} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 4,
                padding: '3px 10px', color: '#fff', fontSize: 10, fontFamily: sans,
                cursor: 'pointer', fontWeight: 600,
              }}>Close Preview</button>
            </div>
            <InvestorPage sbData={sbData} />
          </div>
        </CurrencyContext.Provider>
        </LanguageContext.Provider>
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={theme}>
      <LanguageContext.Provider value={lang}>
      <CurrencyContext.Provider value={currencyCtx}>
        <div style={{ background: theme.bg, minHeight: '100vh', transition: 'background 0.2s' }}>
          {/* Controls passed to AppContent header */}
          {/* MFA Enrollment Modal */}
          {showMfaSetup && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.4)', zIndex: 300,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '32px 28px',
                width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', position: 'relative',
              }}>
                <button onClick={() => setShowMfaSetup(false)} style={{
                  position: 'absolute', top: 12, right: 16, background: 'none',
                  border: 'none', fontSize: 18, cursor: 'pointer', color: '#9E9E9E',
                }}>x</button>
                <MfaEnroll onComplete={() => { setShowMfaSetup(false); window.location.reload(); }} />
              </div>
            </div>
          )}
          {/* Team management moved to Settings > Users & Roles */}
          <UsdBanner />
          <AppContent companyConfig={companyConfig} activeProfile={activeProfile} onMonthChange={setSelectedMonthForFx} sbData={sbData} onLiveRateLoaded={setLiveRate} userInitials={getUserInitials(user)} onNavigateRef={navigateRef} isDark={isDark} setIsDark={(v) => { setIsDark(v); localStorage.setItem('finkloz_theme', v ? 'dark' : 'light'); }} lang={lang} setLang={(l) => { setLang(l); localStorage.setItem('closely_lang', l); }} currency={currency} setCurrency={(c) => { setCurrency(c); localStorage.setItem('finkloz_currency', c); }} hasMfaEnrolled={hasMfaEnrolled} showMfaSetup={showMfaSetup} setShowMfaSetup={setShowMfaSetup} headerControls={
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Sage sync — affects Close & Review, Bank Feed, and FP&A,
                      so it lives at the app level (not inside any tab).
                      Hidden for read-only roles and the investor preview. */}
                  {!['investor', 'board'].includes(activeProfile?.role) &&
                    !window.location.search.includes('preview=investor') && (
                      <SageSyncButton companyId={companyConfig?.companyId} />
                    )}
                  <CurrencyToggle currency={currency} onToggle={setCurrency} fxRate={fxRate} fxDate={selectedMonthForFx} />
                  <button onClick={() => window.open(window.location.origin + '?preview=investor', '_blank', 'width=1400,height=900')} title="Preview as Investor" style={{
                    border: `1px solid ${theme.border}`,
                    background: theme.inputBg,
                    borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.textSecondary,
                  }}><Eye size={14} /></button>
                  <button onClick={() => navigateRef.current && navigateRef.current('settings')} title="Settings" style={{
                    border: `1px solid ${theme.border}`,
                    background: theme.inputBg,
                    borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.textSecondary,
                  }}><Settings size={14} /></button>
                  <button onClick={() => setActiveCompany(null)} title="Switch company" style={{
                    border: `1px solid ${theme.border}`, background: theme.inputBg,
                    borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                    fontSize: 11, fontWeight: 500, fontFamily: sans, color: theme.textSecondary,
                  }}>Switch</button>
                  <button onClick={signOut} title="Sign out" style={{
                    border: `1px solid ${theme.border}`, background: theme.inputBg,
                    borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                    fontSize: 11, fontWeight: 500, fontFamily: sans, color: theme.textSecondary,
                  }}>Sign out</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ThemeToggle isDark={isDark} onToggle={() => setIsDark(!isDark)} />
                </div>
              </div>
            </>
          } />
        </div>
      </CurrencyContext.Provider>
      </LanguageContext.Provider>
    </ThemeContext.Provider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <AppShell />
      </CompanyProvider>
    </AuthProvider>
  );
}
